import { useBranding } from '@/contexts/BrandingContext';
import { ArrowLeft, Building2, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';

import ChatWidgetLoader from '@/components/ChatWidgetLoader';

export default function TermsAndConditions() {
  const { branding } = useBranding();

  return (
    <div className="min-h-screen bg-gray-50">
      <ChatWidgetLoader />
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            {branding.logo_url ? (
              <img src={branding.logo_url} alt={branding.platform_name} className="h-8 object-contain" />
            ) : (
              <div className="p-2 bg-violet-600 rounded-lg">
                <Building2 className="w-5 h-5 text-white" />
              </div>
            )}
            <span className="font-semibold text-gray-900">{branding.platform_name}</span>
          </Link>
          <Link 
            to="/" 
            className="flex items-center gap-2 text-gray-600 hover:text-violet-600 transition-colors"
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Home</span>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 md:p-12">
          <div className="flex items-center gap-3 mb-8">
            <div className="p-3 bg-violet-100 rounded-lg">
              <FileText className="w-6 h-6 text-violet-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900" data-testid="terms-title">Terms and Conditions</h1>
          </div>
          
          <div className="prose prose-gray max-w-none">
            <p className="text-gray-600 mb-8">
              Welcome to Trackly.net (&quot;Trackly&quot;, &quot;we&quot;, &quot;our&quot;, &quot;us&quot;). Trackly is a SaaS-based attendance and workforce monitoring platform that includes features such as GPS tracking, idle state detection, check-in/check-out, break monitoring, and activity tracking.
            </p>
            <p className="text-gray-600 mb-8">
              By accessing or using Trackly, you agree to be bound by these Terms and Conditions.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">1. Eligibility</h2>
            <p className="text-gray-600 mb-6">
              You must be at least 18 years old and authorized by your organization to use Trackly.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">2. Service Description</h2>
            <p className="text-gray-600 mb-4">Trackly provides:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
              <li>Employee check-in and check-out tracking</li>
              <li>GPS-based location tracking during work sessions</li>
              <li>Idle time and activity monitoring</li>
              <li>Break time logging</li>
              <li>Attendance and productivity reports</li>
            </ul>
            <p className="text-gray-600 mb-6">
              We reserve the right to modify or discontinue any part of the service at any time.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">3. User Responsibilities</h2>
            <p className="text-gray-600 mb-4">You agree to:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
              <li>Use Trackly only for lawful business purposes</li>
              <li>Ensure employees are informed and have consented to being tracked</li>
              <li>Keep your account credentials secure</li>
              <li>Not misuse the system for surveillance outside working hours</li>
            </ul>
            <p className="text-gray-600 mb-6">
              You are solely responsible for compliance with labor and privacy laws in your jurisdiction.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">4. Employee Consent</h2>
            <p className="text-gray-600 mb-4">Organizations must:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
              <li>Clearly inform employees about GPS tracking, idle monitoring, and data collection</li>
              <li>Obtain written or digital consent where required by law</li>
              <li>Use tracking features only during working hours</li>
            </ul>
            <p className="text-gray-600 mb-6">
              Trackly is not liable for misuse of the system.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">5. Data Accuracy</h2>
            <p className="text-gray-600 mb-4">While we strive for accuracy, Trackly does not guarantee:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
              <li>GPS precision at all times</li>
              <li>Perfect idle state detection</li>
              <li>Uninterrupted service availability</li>
            </ul>
            <p className="text-gray-600 mb-6">
              The platform is provided &quot;as is&quot;.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">6. Prohibited Activities</h2>
            <p className="text-gray-600 mb-4">You may not:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
              <li>Track individuals without their knowledge</li>
              <li>Attempt to reverse engineer the platform</li>
              <li>Use Trackly for illegal monitoring or harassment</li>
              <li>Interfere with system performance or security</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">7. Payments & Subscriptions</h2>
            <p className="text-gray-600 mb-4">If you use paid plans:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
              <li>Fees are billed per user or as per your chosen plan</li>
              <li>Payments are non-refundable unless stated otherwise</li>
              <li>We may change pricing with prior notice</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">8. Termination</h2>
            <p className="text-gray-600 mb-4">We may suspend or terminate access if:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
              <li>These Terms are violated</li>
              <li>There is misuse of tracking features</li>
              <li>There is illegal or unethical activity</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">9. Limitation of Liability</h2>
            <p className="text-gray-600 mb-4">Trackly shall not be liable for:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
              <li>Employment disputes</li>
              <li>Data misuse by employers</li>
              <li>Loss of business, profits, or productivity</li>
            </ul>
            <p className="text-gray-600 mb-6">
              Use of the platform is at your own risk.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">10. Governing Law</h2>
            <p className="text-gray-600 mb-6">
              These Terms shall be governed by the laws of your registered business jurisdiction.
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-6 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center text-gray-500 text-sm">
          © {new Date().getFullYear()} {branding.platform_name}. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
